import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  AppBar,
  Toolbar,
  IconButton,
} from '@mui/material';
import { Search, Logout, CheckCircle, RadioButtonUnchecked, DirectionsCar, Assignment, AssignmentTurnedIn, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/apiService';
import homepageLogo from '../assets/homepage_logo.png';

// Styled components for modern design
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  borderRadius: theme.spacing(3),
  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
  border: '1px solid rgba(0, 0, 0, 0.05)',
}));

const SearchSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: theme.spacing(2),
    fontSize: '1rem',
    '& fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.1)',
      borderWidth: 1,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.light,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '1.05rem',
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
  '& .MuiOutlinedInput-input': {
    padding: theme.spacing(2),
    fontSize: '1rem',
    fontWeight: 500,
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  overflow: 'hidden',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  '& .MuiTable-root': {
    backgroundColor: '#ffffff',
  },
  '& .MuiTableHead-root': {
    backgroundColor: '#f8f9fa',
  },
  '& .MuiTableCell-head': {
    fontWeight: 600,
    fontSize: '1rem',
    color: theme.palette.text.primary,
    borderBottom: '2px solid #e9ecef',
  },
  '& .MuiTableRow-root': {
    '&:hover': {
      backgroundColor: '#f8f9fa',
    },
    '&.Mui-selected': {
      backgroundColor: '#e3f2fd',
      '&:hover': {
        backgroundColor: '#bbdefb',
      },
    },
  },
  '& .MuiTableCell-body': {
    fontSize: '0.95rem',
    fontWeight: 500,
  },
}));

const TitleSection = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(4),
  '& .main-title': {
    fontWeight: 700,
    background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    marginBottom: theme.spacing(1),
  },
  '& .sub-title': {
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
}));

const ROWS_PER_PAGE = 5;

// Custom Pagination Component
const CustomPagination = ({ totalItems, currentPage, rowsPerPage, onPageChange }) => {
  const totalPages = Math.ceil(totalItems / rowsPerPage);
  const from = currentPage * rowsPerPage + 1;
  const to = Math.min((currentPage + 1) * rowsPerPage, totalItems);

  // Calculate which page numbers to show
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if 5 or fewer
      for (let i = 0; i < totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Calculate start and end of visible range
      let start = Math.max(0, currentPage - 2);
      let end = Math.min(totalPages - 1, currentPage + 2);
      
      // Adjust if at the beginning
      if (currentPage < 2) {
        end = maxVisiblePages - 1;
      }
      // Adjust if at the end
      if (currentPage > totalPages - 3) {
        start = totalPages - maxVisiblePages;
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  };

  const pageNumbers = getPageNumbers();
  const showLeftEllipsis = totalPages > 5 && pageNumbers[0] > 0;
  const showRightEllipsis = totalPages > 5 && pageNumbers[pageNumbers.length - 1] < totalPages - 1;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 16px',
        borderTop: '1px solid #e9ecef',
        backgroundColor: '#fafafa',
      }}
    >
      {/* Left side - record count */}
      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
        {from} to {to} of {totalItems} records
      </Typography>

      {/* Right side - page navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {/* Previous button */}
        <IconButton
          size="small"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 0}
          sx={{ 
            color: currentPage === 0 ? 'text.disabled' : 'primary.main',
            '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' }
          }}
        >
          <ChevronLeft />
        </IconButton>

        {/* Left ellipsis */}
        {showLeftEllipsis && (
          <Typography variant="body2" sx={{ px: 1, color: 'text.secondary' }}>...</Typography>
        )}

        {/* Page numbers */}
        {pageNumbers.map((pageNum) => (
          <Button
            key={pageNum}
            size="small"
            variant={pageNum === currentPage ? 'contained' : 'text'}
            onClick={() => onPageChange(pageNum)}
            sx={{
              minWidth: 32,
              height: 32,
              padding: 0,
              fontSize: '0.875rem',
              fontWeight: pageNum === currentPage ? 600 : 400,
              ...(pageNum === currentPage ? {} : {
                color: 'text.primary',
                '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' }
              })
            }}
          >
            {pageNum + 1}
          </Button>
        ))}

        {/* Right ellipsis */}
        {showRightEllipsis && (
          <Typography variant="body2" sx={{ px: 1, color: 'text.secondary' }}>...</Typography>
        )}

        {/* Next button */}
        <IconButton
          size="small"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages - 1}
          sx={{ 
            color: currentPage >= totalPages - 1 ? 'text.disabled' : 'primary.main',
            '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' }
          }}
        >
          <ChevronRight />
        </IconButton>
      </Box>
    </Box>
  );
};

const HomePage = () => {
  const [contractNo, setContractNo] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleSearch = async () => {
    if (!contractNo.trim()) {
      setError('Please enter a Contract No. or License No.');
      return;
    }

    setLoading(true);
    setError('');
    setSearchResults([]);
    setSelectedVehicle(null);
    setPage(0); // Reset to first page on new search

    try {
      const vehicles = await apiService.searchVehicle(contractNo.trim());
      if (vehicles && vehicles.length > 0) {
        setSearchResults(vehicles);
      } else {
        setError('No vehicles found');
      }
    } catch (err) {
      setError(err.message || 'Vehicle search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleSelectVehicle = (vehicle) => {
    // Toggle selection: if clicking the same vehicle, unselect it
    if (selectedVehicle?.VehicleID === vehicle.VehicleID) {
      setSelectedVehicle(null);
    } else {
      setSelectedVehicle(vehicle);
    }
  };

  const handleNext = () => {
    if (selectedVehicle) {
      navigate('/inspection', { 
        state: { 
          vehicle: selectedVehicle,
          isSubmitted: selectedVehicle.isSubmitted === 1
        } 
      });
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <Box>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            eScoreCard - Vehicle Inspection
          </Typography>
          <Typography variant="body1" sx={{ mr: 2 }}>
            Welcome, {user?.name || user?.email}
          </Typography>
          <IconButton color="inherit" onClick={handleLogout}>
            <Logout />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <StyledPaper>
          <TitleSection>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <img 
                src={homepageLogo} 
                alt="eScoreCard Logo" 
                style={{
                  width: '300px',
                  height: 'auto',
                  maxWidth: '100%',
                }} 
              />
            </Box>
            <Typography variant="h3" className="main-title" gutterBottom>
              eScoreCard (UAT)
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 3 }}>
              <DirectionsCar sx={{ fontSize: 32, color: 'primary.main', opacity: 0.7 }} />
              <Typography variant="h5" className="sub-title">
                Vehicle Inspection System
              </Typography>
            </Box>
          </TitleSection>

          <SearchSection>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }}>
              Search Vehicle by Contract No. or License No.
            </Typography>
            <Box display="flex" gap={3} alignItems="center">
              <StyledTextField
                fullWidth
                label="Contract No. or License No."
                value={contractNo}
                onChange={(e) => setContractNo(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                disabled={loading}
              />
              <Button
                variant="contained"
                size="medium"
                startIcon={loading ? <CircularProgress size={20} /> : <Search />}
                onClick={handleSearch}
                disabled={loading}
                sx={{ 
                  minWidth: 120,
                  borderRadius: 2,
                  padding: '10px 24px',
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                Search
              </Button>
            </Box>
          </SearchSection>

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                borderRadius: 2,
                '& .MuiAlert-message': {
                  fontSize: '1rem',
                  fontWeight: 500,
                },
              }}
            >
              {error}
            </Alert>
          )}

          {searchResults.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Typography 
                variant="h6" 
                gutterBottom 
                sx={{ 
                  fontWeight: 600, 
                  color: 'text.primary',
                  mb: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <CheckCircle sx={{ color: 'success.main' }} />
                Search Results
              </Typography>
              <StyledTableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Select</TableCell>
                      <TableCell>Make 品牌</TableCell>
                      <TableCell>Car-In Date 入庫日期</TableCell>
                      <TableCell>License No. 車牌號碼</TableCell>
                      <TableCell>Contract No. 合約編號</TableCell>
                      <TableCell>Status 狀態</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {searchResults
                      .slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE)
                      .map((vehicle, index) => (
                      <TableRow 
                        key={vehicle.VehicleID || index}
                        selected={selectedVehicle?.VehicleID === vehicle.VehicleID}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleSelectVehicle(vehicle)}
                      >
                        <TableCell>
                          {selectedVehicle?.VehicleID === vehicle.VehicleID ? (
                            <CheckCircle color="primary" />
                          ) : (
                            <RadioButtonUnchecked color="disabled" />
                          )}
                        </TableCell>
                        <TableCell>{vehicle.Make || 'N/A'}</TableCell>
                        <TableCell>
                          {vehicle.CarInDate 
                            ? new Date(vehicle.CarInDate).toLocaleDateString()
                            : 'N/A'
                          }
                        </TableCell>
                        <TableCell>{vehicle.LicenseNo || 'N/A'}</TableCell>
                        <TableCell>{vehicle.ContractNo || 'N/A'}</TableCell>
                        <TableCell>
                          {vehicle.isSubmitted === 1 ? (
                            <Box display="flex" alignItems="center" gap={1} color="success.main">
                              <AssignmentTurnedIn sx={{ fontSize: 18 }} />
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                Submitted
                              </Typography>
                            </Box>
                          ) : (
                            <Box display="flex" alignItems="center" gap={1} color="warning.main">
                              <Assignment sx={{ fontSize: 18 }} />
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                Not Submitted
                              </Typography>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {searchResults.length > ROWS_PER_PAGE && (
                  <CustomPagination
                    totalItems={searchResults.length}
                    currentPage={page}
                    rowsPerPage={ROWS_PER_PAGE}
                    onPageChange={handleChangePage}
                  />
                )}
              </StyledTableContainer>

              {selectedVehicle && (
                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    size="medium"
                    onClick={handleNext}
                    sx={{
                      borderRadius: 2,
                      padding: '10px 24px',
                      fontSize: '1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                      boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #388e3c 0%, #4caf50 100%)',
                        boxShadow: '0 6px 16px rgba(76, 175, 80, 0.4)',
                        transform: 'translateY(-1px)',
                      },
                    }}
                  >
                    {selectedVehicle.isSubmitted === 1
                      ? 'Review Submitted Inspection'
                      : 'Next - Start Inspection'
                    }
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </StyledPaper>
      </Container>
    </Box>
  );
};

export default HomePage;